import { isStoryLikePostType } from "./feedMedia";

function firstLine(value: unknown): string {
  return String(value ?? "")
    .trim()
    .split("\n")[0]
    ?.trim() || "";
}

function normalizeHandle(value: unknown): string {
  return firstLine(value)
    .replace(/^@+/, "")
    .replace(/\s+/g, "")
    .toLowerCase();
}

function postHandleCandidates(post: any): string[] {
  if (!post || typeof post !== "object") return [];
  return [
    post.username,
    post.instagram_username,
    post.ig_username,
    post.user_name,
    post.handle,
    post.instagram_user,
  ]
    .map(normalizeHandle)
    .filter(Boolean);
}

function isHandleShaped(raw: string, normalized: string): boolean {
  if (!raw || !normalized) return false;
  if (/\s/.test(raw)) return false;
  return /^[a-z0-9._]{2,30}$/i.test(normalized);
}

const SOCIAL_POST_TYPES = new Set([
  "STORY",
  "HIGHLIGHTS",
  "REELS",
  "FEED",
  "VIDEO",
  "TIKTOK",
  "SHORT",
  "SHORTS",
]);

/** Instagram ingest often stores the IG handle in title fields. */
export function isInstagramHandleLikeTitle(
  text: unknown,
  post?: any,
): boolean {
  const raw = firstLine(text);
  if (!raw) return false;
  const n = normalizeHandle(raw);
  if (!n) return false;
  if (postHandleCandidates(post).includes(n)) return true;
  if (!isHandleShaped(raw, n)) return false;

  const type = String(post?.type || "").trim().toUpperCase();
  if (isStoryLikePostType(type) || SOCIAL_POST_TYPES.has(type)) return true;

  const caption = normalizeHandle(post?.caption);
  const shop = normalizeHandle(post?.shop_title);
  const product = normalizeHandle(post?.product_name);
  const content = normalizeHandle(post?.content_title);
  // Same handle stuffed into caption / every title column (shop + LiB products).
  if (caption && caption === n) return true;
  const titleHits = [shop, product, content].filter((v) => v === n).length;
  if (titleHits >= 2) return true;

  const platform = String(post?.platform || "").trim().toLowerCase();
  if (platform === "instagram" && (caption === n || shop === n || product === n)) {
    return true;
  }
  // Product tiles often only have product_name = IG handle, on Amazon/LTK/etc.
  // Real product names are Title Case or have spaces; handles are lowercase.
  if (raw === raw.toLowerCase()) return true;
  return false;
}

/**
 * LTK / ShopMy / Amazon storefront OG titles that got scraped into product_name.
 * These spill as long vertical strips in the Products masonry when left as labels.
 */
export function isAffiliatePageTitleLike(text: unknown): boolean {
  const raw = firstLine(text);
  if (!raw) return false;
  if (/^follow$/i.test(raw)) return true;
  if (/^ltk\s*\|/i.test(raw)) return true;
  if (/^shopmy\s*\|/i.test(raw)) return true;
  if (/\bon\s+ltk\b/i.test(raw)) return true;
  if (/on the ltk app/i.test(raw)) return true;
  if (/shop creator looks/i.test(raw)) return true;
  if (/fashion,\s*home,\s*beauty/i.test(raw)) return true;
  if (/million people love ltk/i.test(raw)) return true;
  if (/growth platform for elite creators/i.test(raw)) return true;
  if (/discover why .*ltk/i.test(raw)) return true;
  // Company SPA OG titles scraped from browsable/searchbubble collection pages.
  if (/^(browsable|search\s*bubble)\b/i.test(raw) && /one\s*link/i.test(raw)) {
    return true;
  }
  if (/^browsable\s*[-–—]/i.test(raw) && /whole\s+world/i.test(raw)) {
    return true;
  }
  // Social captions scraped as product_name (hashtags / LTK tip spam).
  if (/#ltk/i.test(raw)) return true;
  if ((raw.match(/#/g) || []).length >= 2) return true;
  return false;
}

/** Title is just the creator handle / display username (e.g. "BellaBranchDIY"). */
export function isCreatorUsernameTitle(
  text: unknown,
  post?: any,
  creatorUsername?: string | null,
): boolean {
  const raw = firstLine(text);
  if (!raw) return false;
  const token = raw.replace(/^@+/, "").trim();
  if (!token || /\s/.test(token)) return false;
  const n = normalizeHandle(token);
  if (!n || !isHandleShaped(token, n)) return false;

  const candidates = new Set(postHandleCandidates(post));
  const creator = normalizeHandle(creatorUsername);
  if (creator) candidates.add(creator);
  return candidates.has(n);
}

/**
 * Public card label — skip IG handles so `/anghow` does not repeat
 * `angelahowenstein` under stories, reels, or shop tiles.
 *
 * Never use `caption` here: IG/TikTok captions are full post copy and must not
 * appear under Link-in-Bio / feed tiles (that path used to show nothing when
 * title fields were empty or handle-like).
 */
export function displayFeedCardTitle(
  post: any,
  opts?: { creatorUsername?: string | null },
): string {
  if (!post || typeof post !== "object") return "";
  const candidates = [
    post.shop_title,
    post.content_title,
    post.product_name,
    post.title,
    post.name,
    post.table_name,
  ];
  for (const value of candidates) {
    const line = firstLine(value);
    if (!line) continue;
    if (isInstagramHandleLikeTitle(line, post)) continue;
    if (isAffiliatePageTitleLike(line)) continue;
    if (isCreatorUsernameTitle(line, post, opts?.creatorUsername)) continue;
    return line.slice(0, 80);
  }
  return "";
}

/** Keep a picked title only when it is not an IG handle or affiliate page chrome. */
export function sanitizePublicCardTitle(
  text: unknown,
  post?: any,
  opts?: { creatorUsername?: string | null },
): string {
  const line = firstLine(text);
  if (!line) return displayFeedCardTitle(post, opts);
  if (isInstagramHandleLikeTitle(line, post)) {
    return displayFeedCardTitle(post, opts);
  }
  if (isAffiliatePageTitleLike(line)) return displayFeedCardTitle(post, opts);
  if (isCreatorUsernameTitle(line, post, opts?.creatorUsername)) {
    return displayFeedCardTitle(post, opts);
  }
  return line.slice(0, 80);
}

const CONTENT_PLATFORMS = new Set([
  "instagram",
  "tiktok",
  "youtube",
  "linkedin",
  "twitter",
  "x",
  "podcast",
  "pinterest",
  "newsletter",
  "misc-content",
]);

export function isContentPlatform(platform: unknown): boolean {
  return CONTENT_PLATFORMS.has(firstLine(platform).toLowerCase());
}

export function contentClickNameIsPlatform(
  name: unknown,
  platform: unknown,
): boolean {
  const label = firstLine(name).toLowerCase();
  if (!label) return true;
  const platformLabel = firstLine(platform).toLowerCase();
  if (platformLabel && label === platformLabel) return true;
  return isContentPlatform(label);
}

/** Content posts use content_title / caption the way shop posts use product_name. */
export function contentCarouselClickName(
  post: any,
  carouselTitle?: unknown,
): string {
  const platform = firstLine(post?.platform).toLowerCase();
  const heading = firstLine(carouselTitle).toLowerCase();
  const pick = (value: unknown) => {
    const line = firstLine(value);
    if (!line) return "";
    const lower = line.toLowerCase();
    if (platform && lower === platform) return "";
    if (isContentPlatform(line)) return "";
    if (heading && lower === heading) return "";
    if (isInstagramHandleLikeTitle(line, post)) return "";
    if (isAffiliatePageTitleLike(line)) return "";
    return line.slice(0, 80);
  };
  return (
    pick(post?.name) ||
    pick(post?.content_title) ||
    pick(post?.shop_title) ||
    pick(post?.title) ||
    
    pick(post?.caption) ||
    pick(post?.product_name) ||
    pick(post?.table_name) ||
    ""
  );
}
